import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ApiService } from '../../services/api.service';
import { SegmentStatisticsApiResponse } from '../../shared/types';
import { catchError, of } from 'rxjs';

/**
 * Displays segment-related metrics (total, active, invalid, unsubscribed) by fetching statistics from the backend and exposing them as an array of metric cards for presentation.
 * @component
 * @selector app-segments-metrics
 * @example
 *   <app-segments-metrics></app-segments-metrics>
 * @input N/A N/A - No input properties; the component fetches its own data on initialization.
 * @output N/A N/A - No output events are emitted by this component.
 *
 * Purpose:
 *   - Fetches segment statistics from the API and maps the response into a metrics array used by the template.
 *   - Manages loading and error state to allow the template to display spinners, errors, or metrics accordingly.
 *
 * Key properties:
 *   - metrics: any[] - Array of metric objects (title, value, gradient, icon) used for rendering metric cards.
 *   - loading: boolean - True while data is being fetched; false after completion or on error.
 *   - error: string | null - Error message when fetching fails or when the server response is invalid.
 *
 * Key methods:
 *   - loadSegmentStatistics(): void
 *       Initiates the API call to 'segment/statistics', sets loading and error states, handles API errors,
 *       and updates metrics on successful response. Uses catchError to handle request failures and sets a user-facing error message.
 *
 *   - private updateMetrics(data: any): void
 *       Maps the API response payload to the internal metrics array, providing defaults ('0') when fields are missing,
 *       and sets visual properties (gradients and icons) for each metric card.
 *
 * Lifecycle hooks:
 *   - ngOnInit(): void
 *       Called once on component initialization. Triggers loadSegmentStatistics() to populate metrics as soon as the component is created.
 */
@Component({
  selector: 'app-segments-metrics',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './segments-metrics.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './segments-metrics.component.scss',
})
export class SegmentsMetricsComponent implements OnInit {
  metrics: any[] = [
    {
      title: 'Total Segments',
      value: '-',
      gradient: 'linear-gradient(180deg, rgba(169, 223, 133, 1) 0%, rgba(109, 165, 72, 1) 100%)',
      icon: 'assets/images/segments/metric-bg.png',
    },
    {
      title: 'Active Segments',
      value: '-',
      gradient: 'linear-gradient(180deg, rgba(163, 199, 255, 1) 0%, rgba(81, 134, 217, 1) 100%)',
      icon: 'assets/images/segments/metric-bg.png',
    },
    {
      title: 'Invalid Users',
      value: '-',
      gradient: 'linear-gradient(180deg, rgba(175, 165, 246, 1) 0%, rgba(136, 120, 242, 1) 100%)',
      icon: 'assets/images/segments/metric-bg.png',
    },
    {
      title: 'Unsubscribed Users',
      value: '-',
      gradient: 'linear-gradient(180deg, rgba(246, 190, 142, 1) 0%, rgba(215, 139, 73, 1) 100%)',
      icon: 'assets/images/segments/metric-bg.png',
    },
  ];
  loading = true;
  error: string | null = null;

  private static cachedData: any = null;
  private static hasLoaded = false;

  constructor(private apiService: ApiService) {}

  ngOnInit(): void {
    this.loadSegmentStatistics();
  }

  /**
   * Fetches segment statistics from the API, updates metrics on success, and manages loading and error state.
   * @example
   * oadSegmentStatistics()
   * // undefined (side-effect: updates component state or sets this.error)
   * @returns {void} Performs the fetch and updates component state; does not return a value.
   */
  loadSegmentStatistics(): void {
    if (SegmentsMetricsComponent.hasLoaded) {
      if (SegmentsMetricsComponent.cachedData) {
        this.updateMetrics(SegmentsMetricsComponent.cachedData);
      } else {
        this.error = 'Failed to load segment statistics';
      }
      this.loading = false;
      return;
    }

    this.loading = true;
    this.error = null;

    this.apiService
      .get<SegmentStatisticsApiResponse>('segment/statistics')
      .pipe(
        catchError(error => {
          console.error('Error fetching segment statistics:', error);
          this.error = 'Failed to load segment statistics';
          this.loading = false;
          return of(null);
        })
      )
      .subscribe(response => {
        this.loading = false;
        if (response && response.status && response.data) {
          SegmentsMetricsComponent.cachedData = response.data;
          SegmentsMetricsComponent.hasLoaded = true;
          this.updateMetrics(response.data);
        } else {
          SegmentsMetricsComponent.hasLoaded = true;
          SegmentsMetricsComponent.cachedData = null;
          this.error = 'Invalid response from server';
        }
      });
  }

  /**
   * Update the component's metrics array from the given data object, applying defaults and styling for each metric.
   * @example
   * updateMetrics({ total_segments: '10', active_segments: '7', inactive_customers: '2', unsubscribed_customers: '1' })
   * undefined
   * @param {{any}} {{data}} - Data object containing metric values (total_segments, active_segments, inactive_customers, unsubscribed_customers).
   * @returns {{void}} No return value; the method updates the component's metrics property in place.
   **/
  private updateMetrics(data: any): void {
    this.metrics = [
      {
        title: 'Total Segments',
        value: data.total_segments || '0',
        gradient: 'linear-gradient(180deg, rgba(169, 223, 133, 1) 0%, rgba(109, 165, 72, 1) 100%)',
        icon: 'assets/images/segments/metric-bg.png',
      },
      {
        title: 'Active Segments',
        value: data.active_segments || '0',
        gradient: 'linear-gradient(180deg, rgba(163, 199, 255, 1) 0%, rgba(81, 134, 217, 1) 100%)',
        icon: 'assets/images/segments/metric-bg.png',
      },
      {
        title: 'Invalid Users',
        value: data.inactive_customers || '0',
        gradient: 'linear-gradient(180deg, rgba(175, 165, 246, 1) 0%, rgba(136, 120, 242, 1) 100%)',
        icon: 'assets/images/segments/metric-bg.png',
      },
      {
        title: 'Unsubscribed Users',
        value: data.unsubscribed_customers || '0',
        gradient: 'linear-gradient(180deg, rgba(246, 190, 142, 1) 0%, rgba(215, 139, 73, 1) 100%)',
        icon: 'assets/images/segments/metric-bg.png',
      },
    ];
  }

  trackByIndex(index: number): number {
    return index;
  }
  trackByValue(_: number, item: any): any {
    return item?.value ?? item;
  }
  trackById(_: number, item: any): any {
    return item?.id ?? item;
  }
  trackByString(_: number, s: any): any {
    return s;
  }
}
